import { useCallback, useEffect, useRef, useState } from 'react';
import { UtteranceDetector, encodeWav } from '@mia-platform-internal/chatbot-widget';
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

function toBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('The recording could not be read.'));
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Hands-free prompt for the Home box: one click starts listening, and the first sentence the user says — found by its
 * silences, no further clicks — is turned into text (`POST /api/audio/transcribe` on adk-be-app, as the chat does) and
 * handed to `onText`, which sends it. Listening then stops, because sending leaves Home for the chat.
 *
 * `phase` is 'off' | 'listening' | 'hearing' | 'transcribing'; `level` (0..1) drives the wave; `error` is null,
 * 'permission', 'unsupported' or 'failed' until the next try.
 */
export default function useHandsFreePrompt(onText) {
  const [phase, setPhaseState] = useState('off');
  const [level, setLevel] = useState(0);
  const [error, setError] = useState(null);
  const phaseRef = useRef('off');
  const onTextRef = useRef(onText);
  onTextRef.current = onText;
  const resourcesRef = useRef(null);
  const detectorRef = useRef(new UtteranceDetector());
  const lastLevelAtRef = useRef(0);

  const setPhase = useCallback((next) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  const release = useCallback(() => {
    const resources = resourcesRef.current;
    resourcesRef.current = null;
    if (!resources) { return; }
    resources.node.onaudioprocess = null;
    resources.source.disconnect();
    resources.node.disconnect();
    resources.stream.getTracks().forEach((track) => track.stop());
    resources.context.close?.().catch?.(() => undefined);
  }, []);

  const stop = useCallback(() => {
    release();
    setLevel(0);
    setPhase('off');
  }, [release, setPhase]);

  useEffect(() => stop, [stop]);

  const handleUtterance = useCallback(async (samples, sampleRate) => {
    setPhase('transcribing');
    try {
      const clip = encodeWav(samples, sampleRate);
      const response = await getAuthorizedFetch()(`${config.vfsAgentUrl}/api/audio/transcribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audio_base64: await toBase64(clip), mime_type: 'audio/wav' })
      });
      if (!response.ok) { throw new Error(`transcription failed (${response.status})`); }
      const { text } = await response.json();
      if (phaseRef.current !== 'transcribing') { return; }
      if (!text || !text.trim()) {
        detectorRef.current.reset();
        setPhase('listening');
        return;
      }
      stop();
      onTextRef.current(text.trim());
    } catch (_error) {
      if (phaseRef.current === 'off') { return; }
      setError('failed');
      detectorRef.current.reset();
      setPhase('listening');
    }
  }, [setPhase, stop]);

  const start = useCallback(async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof AudioContext === 'undefined') {
      setError('unsupported');
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch (_error) {
      setError('permission');
      return;
    }
    const context = new AudioContext();
    const source = context.createMediaStreamSource(stream);
    const node = context.createScriptProcessor(4096, 1, 1);
    detectorRef.current.reset();
    node.onaudioprocess = (event) => {
      const current = phaseRef.current;
      if (current !== 'listening' && current !== 'hearing') { return; }
      const now = performance.now();
      const step = detectorRef.current.push(new Float32Array(event.inputBuffer.getChannelData(0)), context.sampleRate, now);
      if (now - lastLevelAtRef.current > 80) {
        lastLevelAtRef.current = now;
        setLevel(step.level);
      }
      if (step.event?.type === 'start') { setPhase('hearing'); }
      if (step.event?.type === 'drop') { setPhase('listening'); }
      if (step.event?.type === 'end') { handleUtterance(step.event.samples, step.event.sampleRate); }
    };
    source.connect(node);
    node.connect(context.destination);
    resourcesRef.current = { context, node, source, stream };
    setPhase('listening');
  }, [handleUtterance, setPhase]);

  const toggle = useCallback(() => {
    if (phaseRef.current === 'off') { start(); } else { stop(); }
  }, [start, stop]);

  return { error, level, phase, toggle };
}
